import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Textarea,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useToast,
  useDisclosure,
  Switch,
  Tooltip,
  Badge,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  Collapse,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { QRCodeSVG } from 'qrcode.react';

const AdminQRs = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isFormOpen, setIsFormOpen] = useState(true);
  
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    link: '',
    numero_usos: null,
    fecha_expiracion: '',
    activo: true,
  });
  
  const [codigos, setCodigos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [qrModalData, setQrModalData] = useState(null);
  const formRef = useRef(null);
  
  const BASE_URL = 'https://www.overtaxi.es';
  
  const fetchCodigos = useCallback(async () => {
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const response = await fetch(`${baseURL}/api/codigos-promocionales/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar códigos promocionales');
      }
      
      const data = await response.json();
      setCodigos(data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch códigos promocionales
  useEffect(() => {
    fetchCodigos();
  }, [fetchCodigos]);
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleCodigoBlur = () => {
    // Normalize código: lowercase, replace spaces with hyphens
    if (formData.codigo) {
      const normalized = formData.codigo
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9_-]/g, '');
      setFormData(prev => ({ ...prev, codigo: normalized }));
    }
  };
  
  const getFullUrl = () => {
    if (!formData.codigo) return BASE_URL;
    return `${BASE_URL}?promocode=${formData.codigo}`;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.codigo || !formData.nombre) {
      toast({
        title: 'Error',
        description: 'El código y el nombre son obligatorios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      setSubmitting(true);
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const url = editingId 
        ? `${baseURL}/api/codigos-promocionales/${editingId}/`
        : `${baseURL}/api/codigos-promocionales/`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      // Concatenar la URL base con el link si existe
      const fullLink = formData.link ? `https://www.overtaxi.es${formData.link}` : null;
      
      const dataToSend = {
        ...formData,
        link: fullLink,
        numero_usos: formData.numero_usos || null,
        fecha_expiracion: formData.fecha_expiracion || null,
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar el código promocional');
      }
      
      toast({
        title: 'Éxito',
        description: editingId 
          ? 'Código promocional actualizado correctamente'
          : 'Código promocional creado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Reset form
      setFormData({
        codigo: '',
        nombre: '',
        descripcion: '',
        link: '',
        numero_usos: null,
        fecha_expiracion: '',
        activo: true,
      });
      setEditingId(null);
      setIsFormOpen(false); // Colapsar la card después de guardar
      
      // Refresh list
      fetchCodigos();
      
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleEdit = (codigo) => {
    // Separar la URL base del resto si existe
    let linkPart = codigo.link || '';
    if (linkPart.startsWith('https://www.overtaxi.es')) {
      linkPart = linkPart.replace('https://www.overtaxi.es', '');
    }
    
    // Convertir fecha a formato datetime-local si existe
    let fechaExp = '';
    if (codigo.fecha_expiracion) {
      const date = new Date(codigo.fecha_expiracion);
      fechaExp = date.toISOString().slice(0, 16);
    }
    
    setFormData({
      codigo: codigo.codigo,
      nombre: codigo.nombre,
      descripcion: codigo.descripcion || '',
      link: linkPart,
      numero_usos: codigo.numero_usos || null,
      fecha_expiracion: fechaExp,
      activo: codigo.activo,
    });
    setEditingId(codigo.id);
    setIsFormOpen(true); // Expandir la card del formulario
    
    // Hacer scroll al formulario después de un pequeño delay para asegurar que se expande
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };
  
  const handleCancelEdit = () => {
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: '',
      link: '',
      numero_usos: null,
      fecha_expiracion: '',
      activo: true,
    });
    setEditingId(null);
    setIsFormOpen(false); // Colapsar la card al cancelar
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este código promocional?')) {
      return;
    }
    
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      const response = await fetch(`${baseURL}/api/codigos-promocionales/${id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al eliminar el código promocional');
      }
      
      toast({
        title: 'Éxito',
        description: 'Código promocional eliminado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      fetchCodigos();
      
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  const handleViewQR = (codigo) => {
    setQrModalData(codigo);
    onOpen();
  };
  
  return (
    <Box p={5} className="admin-page-container">
      <Heading size="xl" mb={5} color="white" className="admin-page-title">
        {t('menu_admin_qrs_promocionales')}
      </Heading>
      
      {/* Tabla de códigos existentes */}
      <Box bg="white" p={6} borderRadius="md" shadow="md" mb={8} variant="outline" borderColor="gray.700">
        <Heading size="md" mb={4} color="gray.800">
          Códigos Promocionales Existentes
        </Heading>
        
        {loading ? (
          <Box textAlign="center" py={10} className="admin-loading">
            <Spinner size="xl" />
            <Text mt={4} color="gray.700" className="admin-loading-text">Cargando códigos promocionales...</Text>
          </Box>
        ) : codigos.length === 0 ? (
          <Alert status="info" className="admin-alert-error">
            <AlertIcon />
            No hay códigos promocionales creados aún
          </Alert>
        ) : (
          <Table variant="simple" size="sm" layout="fixed" width="100%" className="admin-table">
            <Thead>
              <Tr>
                <Th w="7%">Acciones</Th>
                <Th w="10%">Código</Th>
                <Th w="12%">Nombre</Th>
                <Th w="15%">Descripción</Th>
                <Th w="13%">Link</Th>
                <Th w="6%" isNumeric>Hits</Th>
                <Th w="7%">Usos</Th>
                <Th w="10%">Expira</Th>
                <Th w="8%">Estado</Th>
                <Th w="12%">Creado</Th>
              </Tr>
            </Thead>
            <Tbody>
              {codigos.map((codigo) => (
                <Tr key={codigo.id}>
                  <Td w="8%" p={1}>
                    <VStack spacing={0}>
                      <Tooltip label="Ver QR completo">
                        <Button
                          size="xs"
                          onClick={() => handleViewQR(codigo)}
                          colorScheme="blue"
                          variant="ghost"
                          w="100%"
                          minW="unset"
                        >
                          👁️
                        </Button>
                      </Tooltip>
                      <Tooltip label="Editar">
                        <Button
                          size="xs"
                          onClick={() => handleEdit(codigo)}
                          colorScheme="orange"
                          variant="ghost"
                          w="100%"
                          minW="unset"
                        >
                          ✏️
                        </Button>
                      </Tooltip>
                      <Tooltip label="Eliminar">
                        <Button
                          size="xs"
                          onClick={() => handleDelete(codigo.id)}
                          colorScheme="red"
                          variant="ghost"
                          w="100%"
                          minW="unset"
                        >
                          🗑️
                        </Button>
                      </Tooltip>
                    </VStack>
                  </Td>
                  <Td w="10%" fontFamily="mono" fontSize="xs" whiteSpace="normal" wordBreak="break-word" color="black">
                    {codigo.codigo}
                  </Td>
                  <Td w="12%" fontWeight="bold" fontSize="sm" whiteSpace="normal" wordBreak="break-word" color="black">
                    {codigo.nombre}
                  </Td>
                  <Td w="15%" fontSize="sm" whiteSpace="normal" wordBreak="break-word" color="black">
                    {codigo.descripcion || '-'}
                  </Td>
                  <Td w="13%" fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                    {codigo.link ? (
                      <a href={codigo.link} target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce' }}>
                        {codigo.link}
                      </a>
                    ) : <Text color="black">-</Text>}
                  </Td>
                  <Td w="6%" isNumeric>
                    <Badge colorScheme="purple" fontSize="sm">
                      {codigo.hits}
                    </Badge>
                  </Td>
                  <Td w="7%" fontSize="xs" whiteSpace="normal" color="black">
                    {codigo.numero_usos ? (
                      <Badge colorScheme="orange" fontSize="sm">{codigo.numero_usos}</Badge>
                    ) : (
                      <Text color="gray.500" fontSize="xs">Ilimitados</Text>
                    )}
                  </Td>
                  <Td w="10%" fontSize="xs" whiteSpace="normal" color="black">
                    {codigo.fecha_expiracion ? (
                      new Date(codigo.fecha_expiracion).toLocaleDateString('es-ES', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      })
                    ) : (
                      <Text color="gray.500" fontSize="xs">-</Text>
                    )}
                  </Td>
                  <Td w="8%">
                    <Badge colorScheme={codigo.activo ? 'green' : 'gray'} fontSize="sm">
                      {codigo.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </Td>
                  <Td w="12%" fontSize="xs" whiteSpace="normal" color="black">
                    {new Date(codigo.created_at).toLocaleDateString('es-ES')}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
      
      {/* Formulario de creación/edición */}
      <Card bg="yellow.50" variant="outline" borderColor="gray.700" ref={formRef}>
        <CardHeader
          cursor="pointer"
          onClick={() => setIsFormOpen(!isFormOpen)}
          _hover={{ bg: 'yellow.100' }}
          borderTopRadius="md"
          borderBottomRadius={isFormOpen ? "none" : "md"}
        >
          <HStack justify="space-between">
            <Heading size="md" color="black">
              {editingId ? 'Editar Código Promocional' : 'Crear Nuevo Código Promocional'}
            </Heading>
            <Text fontSize="2xl">{isFormOpen ? '▲' : '▼'}</Text>
          </HStack>
        </CardHeader>
        
        <Collapse in={isFormOpen} animateOpacity>
          <CardBody>
            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel color="gray.700" className="admin-form-label">Nombre de la Promoción</FormLabel>
                <Input
                  value={formData.nombre}
                  onChange={(e) => handleInputChange('nombre', e.target.value)}
                  placeholder="Promoción Verano 2024"
                  color="black"
                  bg="white"
                  className="admin-form-input"
                />
              </FormControl>
              
              <FormControl>
                <FormLabel color="gray.700" className="admin-form-label">Descripción</FormLabel>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => handleInputChange('descripcion', e.target.value)}
                  placeholder="Descripción opcional de la promoción..."
                  rows={3}
                  color="black"
                  bg="white"
                  className="admin-form-input"
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel color="gray.700" className="admin-form-label">URL del Código Promocional</FormLabel>
                <InputGroup>
                  <InputLeftAddon children={BASE_URL + '?promocode='} color="gray.800" bg="gray.100" />
                  <Input
                    value={formData.codigo}
                    onChange={(e) => handleInputChange('codigo', e.target.value)}
                    onBlur={handleCodigoBlur}
                    placeholder="verano-2024"
                    isDisabled={editingId !== null}
                    color="black"
                    bg="white"
                    className="admin-form-input"
                  />
                </InputGroup>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Solo letras, números, guiones y guiones bajos
                </Text>
              </FormControl>
              
              {/* QR Preview - Movido antes del Link de Redirección */}
              {formData.codigo && (
                <Box
                  p={4}
                  borderWidth={2}
                  borderRadius="md"
                  textAlign="center"
                  borderColor="blue.400"
                  bg="gray.50"
                >
                  <Text fontSize="md" mb={3} fontWeight="bold" color="gray.700">Vista Previa del QR</Text>
                  <QRCodeSVG
                    value={getFullUrl()}
                    size={450}
                    level="H"
                    includeMargin={true}
                  />
                </Box>
              )}
              
              <FormControl>
                <FormLabel color="gray.700" className="admin-form-label">Link de Redirección</FormLabel>
                <InputGroup>
                  <InputLeftAddon children="https://www.overtaxi.es" color="gray.800" bg="gray.100" />
                  <Input
                    value={formData.link}
                    onChange={(e) => handleInputChange('link', e.target.value)}
                    placeholder="/promociones/verano"
                    color="black"
                    bg="white"
                    className="admin-form-input"
                  />
                </InputGroup>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  URL a la que se redirigirá cuando se acceda con este código promocional
                </Text>
              </FormControl>
              
              <FormControl>
                <FormLabel color="gray.700" className="admin-form-label">Número de Usos</FormLabel>
                <NumberInput
                  value={formData.numero_usos || ''}
                  onChange={(valueString) => handleInputChange('numero_usos', valueString ? parseInt(valueString) : null)}
                  min={1}
                >
                  <NumberInputField 
                    placeholder="Ilimitados"
                    color="black"
                    bg="white"
                    className="admin-form-input"
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Número máximo de veces que se puede usar este código (vacío = ilimitado)
                </Text>
              </FormControl>
              
              <FormControl>
                <FormLabel color="gray.700" className="admin-form-label">Fecha de Expiración</FormLabel>
                <Input
                  type="datetime-local"
                  value={formData.fecha_expiracion}
                  onChange={(e) => handleInputChange('fecha_expiracion', e.target.value)}
                  color="black"
                  bg="white"
                  className="admin-form-input"
                />
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Fecha y hora en la que expira el código (vacío = sin expiración)
                </Text>
              </FormControl>
              
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0} color="gray.700" className="admin-form-label">Activo</FormLabel>
                <Switch
                  isChecked={formData.activo}
                  onChange={(e) => handleInputChange('activo', e.target.checked)}
                  colorScheme="green"
                />
              </FormControl>
              
              <HStack spacing={3}>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={submitting}
                  loadingText={editingId ? 'Actualizando...' : 'Creando...'}
                  className="admin-button-primary"
                >
                  {editingId ? 'Actualizar' : 'Crear Código Promocional'}
                </Button>
                
                {editingId && (
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="admin-button-secondary"
                  >
                    Cancelar Edición
                  </Button>
                )}
              </HStack>
            </VStack>
          </form>
          </CardBody>
        </Collapse>
      </Card>
      
      {/* Modal para ver QR en grande */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{qrModalData?.nombre}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} textAlign="center">
            {qrModalData && (
              <VStack spacing={4}>
                <Box>
                  <QRCodeSVG
                    value={qrModalData.url_completa}
                    size={400}
                    level="H"
                    includeMargin={true}
                  />
                </Box>
                <Text fontFamily="mono" fontSize="sm" color="gray.600">
                  {qrModalData.url_completa}
                </Text>
                <Text fontWeight="bold">Hits: {qrModalData.hits}</Text>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminQRs;
